import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  ArrowLeft, ArrowRight, Award, BookOpen,
  Lightbulb, MessageSquareText, Rocket, UsersRound,
} from "lucide-react";
import { GROWTH_QUESTIONS, GROWTH_REQUESTS } from "../../../config/Endpoints.js";
import { getAuthToken } from "../../../utils/Storage.js";
import { getApi, postApi } from "../../utils/api.ts";
import { useUser } from "../../lib/rbac/useUser.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Spinner } from "../../components/ui/spinner/spinner.tsx";
import { Button, Checkbox, RadioGroup, TextArea, TextField } from "../../components/ui/index.ts";
import styles from "./growth-activities-page.module.css";
import ContributionForm from "./contribution/contribution-form.tsx";
import LearningForm from "./learning/learning-form.tsx";
import CertificationForm from "./certification/certification-form.tsx";
import InnovationForm from "./innovation/innovation-form.tsx";
import LaunchingForm from "./launching/launching-form.tsx";
import KnowledgeSharingForm from "./knowledge-sharing/knowledge-sharing-form.tsx";
import LinkifiedText from "./linkified-text.tsx";
import GrowthRequestDetail from "./request-detail.tsx";
import GrowthSubmissionSuccess from "./growth-submission-success.tsx";
import type { GrowthCategory, GrowthRequest } from "./growth-types.ts";

const categories = [
  { id: "CONTRIBUTION", title: "Contribute", description: "I'm ready to make an impact.", icon: UsersRound, tone: "blue" },
  { id: "LEARNING", title: "Learn & Upskill", description: "I want to build new skills.", icon: BookOpen, tone: "purple" },
  { id: "CERTIFICATION", title: "Get Certified", description: "I want to earn a new certification.", icon: Award, tone: "amber" },
  { id: "INNOVATION", title: "Innovate & Improve", description: "I have an idea.", icon: Lightbulb, tone: "mint" },
  { id: "LAUNCHING", title: "Build & Launch", description: "I’ve built something.", icon: Rocket, tone: "pink" },
  { id: "KNOWLEDGE_SHARING", title: "Host & Share Knowledge", description: "I want to share what I know.", icon: MessageSquareText, tone: "indigo" },
] as const;

type QuestionType = "TEXT" | "RATING" | "CHECKBOX" | "RADIO" | "DATE" | "FILE";
type Question = { id: number; question: string; questionType: QuestionType; optionsJson: string | null; sequenceNo: number };
type Answers = Record<number, string>;

const statusLabels: Record<string, string> = {
  RECEIVED: "Received", BEING_REVIEWED: "Being reviewed", IN_PROGRESS: "In progress",
  WAITING_EMPLOYEE: "Waiting for your reply", COMPLETED: "Completed", CLOSED: "Closed",
};

const initiativeTitles: Record<GrowthCategory, string> = {
  CONTRIBUTION: "Interest in a project opportunity",
  LEARNING: "Interest in learning and upskilling",
  CERTIFICATION: "Interest in pursuing a certification",
  INNOVATION: "An idea to innovate and improve",
  LAUNCHING: "An initiative to build and launch",
  KNOWLEDGE_SHARING: "Interest in hosting and sharing knowledge",
};

function parseOptions(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function readChoices(value: string | undefined): string[] {
  try {
    const parsed: unknown = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function displayDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function GrowthActivitiesPage() {
  const user = useUser();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [selected, setSelected] = useState<GrowthCategory | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Answers>({});
  const [requests, setRequests] = useState<GrowthRequest[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(() => {
    const value = Number(new URLSearchParams(window.location.search).get("requestId"));
    return Number.isInteger(value) && value > 0 ? value : null;
  });
  const [error, setError] = useState("");
  const [requestsError, setRequestsError] = useState("");
  const [submittedRequest, setSubmittedRequest] = useState<GrowthRequest | null>(null);
  const createdRequests = useRef<GrowthRequest[]>([]);

  function handleSubmitted(request: GrowthRequest) {
    createdRequests.current = [request, ...createdRequests.current.filter((item) => item.id !== request.id)];
    setRequests((previous) => [request, ...previous.filter((item) => item.id !== request.id)]);
    setRequestsError("");
    setSelected(null);
    setQuestions([]);
    setAnswers({});
    setReviewing(false);
    setSubmittedRequest(request);
  }

  useEffect(() => {
    setBreadcrumbs([{ label: "Growth & Initiatives" }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    setRequestsError("");
    try {
      const response = await getApi<GrowthRequest[]>(GROWTH_REQUESTS, getAuthToken("accessToken"));
      setRequests([
        ...createdRequests.current.filter((created) => !response.data.some((item) => item.id === created.id)),
        ...response.data.map((item) => ({ ...item, displayTitle: item.displayTitle || createdRequests.current.find((created) => created.id === item.id)?.displayTitle })),
      ]);
    } catch (cause) {
      if (createdRequests.current.length === 0) setRequestsError(cause instanceof Error ? cause.message : "Could not load your initiatives.");
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => { void loadRequests(); }, [loadRequests]);

  async function openCategory(category: GrowthCategory) {
    setSelected(category);
    setAnswers({});
    setQuestions([]);
    setReviewing(false);
    setError("");
    setSubmittedRequest(null);
    setDetailId(null);
    if (category === "CONTRIBUTION" || category === "LEARNING" || category === "CERTIFICATION" || category === "INNOVATION" || category === "LAUNCHING" || category === "KNOWLEDGE_SHARING") return;
    setLoadingQuestions(true);
    try {
      const response = await getApi<Question[]>(
        `${GROWTH_QUESTIONS}?growth_category=${encodeURIComponent(category)}`,
        getAuthToken("accessToken"),
      );
      setQuestions(response.data);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not load these questions.");
    } finally {
      setLoadingQuestions(false);
    }
  }

  function updateAnswer(id: number, answer: string) {
    setAnswers((previous) => ({ ...previous, [id]: answer }));
    setError("");
  }

  const selectedCategory = categories.find((category) => category.id === selected);
  const submittedAnswers = useMemo(() => questions
    .filter((question) => (answers[question.id] || "").trim())
    .map((question) => ({ question_id: question.id, answer: answers[question.id].trim() })), [answers, questions]);
  const hasFileQuestion = questions.some((question) => question.questionType === "FILE");

  function startReview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submittedAnswers.length === 0) {
      setError("Answer at least one question to continue.");
      return;
    }
    setReviewing(true);
    setError("");
  }

  async function submitRequest() {
    if (!selected || !user?.id || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await postApi<GrowthRequest>(GROWTH_REQUESTS, getAuthToken("accessToken"), {
        employee_id: user.id,
        growth_category: selected,
        answers: submittedAnswers,
      });
      handleSubmitted(response.data);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not submit your initiative.");
    } finally {
      setSubmitting(false);
    }
  }

  const firstName = user?.name?.trim() || "Your profile";
  const initials = firstName.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
  const visibleRequests = showAll ? requests : requests.slice(0, 3);

  return (
    <PageShell className={styles.page}>
      {((loadingRequests && detailId === null && selected === null && !submittedRequest) || loadingQuestions || submitting) ? <Spinner variant="overlay" label="Loading growth initiatives" /> : null}
      {detailId !== null ? <GrowthRequestDetail requestId={detailId} onBack={() => {
        setDetailId(null);
        const url = new URL(window.location.href);
        if (url.searchParams.has("requestId")) {
          url.searchParams.delete("requestId");
          window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
        }
      }} /> : submittedRequest ? <GrowthSubmissionSuccess
        growthCategory={submittedRequest.growthCategory}
        onBack={() => { setSubmittedRequest(null); }}
        onViewInitiative={() => {
          setDetailId(submittedRequest.id);
          setSubmittedRequest(null);
        }}
      /> : selected === "CONTRIBUTION" ? (
        <ContributionForm
          onBack={() => setSelected(null)}
          onSubmitted={handleSubmitted}
        />
      ) : selected === "LEARNING" ? <LearningForm onBack={() => setSelected(null)} onSubmitted={handleSubmitted} />
        : selected === "CERTIFICATION" ? <CertificationForm onBack={() => setSelected(null)} onSubmitted={handleSubmitted} />
        : selected === "INNOVATION" ? <InnovationForm onBack={() => setSelected(null)} onSubmitted={handleSubmitted} />
        : selected === "LAUNCHING" ? <LaunchingForm onBack={() => setSelected(null)} onSubmitted={handleSubmitted} />
        : selected === "KNOWLEDGE_SHARING" ? <KnowledgeSharingForm onBack={() => setSelected(null)} onSubmitted={handleSubmitted} /> : selectedCategory ? (
        <section className={styles.formCard} aria-labelledby="growth-form-title">
          <button type="button" className={styles.backLink} onClick={() => { setSelected(null); setError(""); }}>
            <ArrowLeft size={16} /> Back to Growth & Initiatives
          </button>
          <div className={styles.formHeading}>
            <span className={`${styles.categoryIcon} ${styles[selectedCategory.tone]}`}><selectedCategory.icon size={23} /></span>
            <div><p className={styles.eyebrow}>YOUR NEXT STEP</p><h1 id="growth-form-title">{selectedCategory.title}</h1><p>{selectedCategory.description}</p></div>
          </div>
          {loadingQuestions ? <p className={styles.stateText}>Loading questions…</p> : null}
          {error ? <p className={styles.error} role="alert">{error}</p> : null}
          {!loadingQuestions && questions.length === 0 && !error ? <p className={styles.stateText}>No questions are available for this category yet.</p> : null}
          {questions.length > 0 && !reviewing ? (
            <form onSubmit={startReview} className={styles.questionForm}>
              {questions.map((question, index) => {
                const options = parseOptions(question.optionsJson);
                const value = answers[question.id] || "";
                return (
                  <div key={question.id} className={styles.question}>
                    <span className={styles.questionNumber}>QUESTION {index + 1}</span>
                    {question.questionType === "TEXT" ? <TextArea id={`growth-${question.id}`} label={question.question} value={value} onChange={(event) => updateAnswer(question.id, event.target.value)} /> : null}
                    {question.questionType === "DATE" ? <TextField id={`growth-${question.id}`} label={question.question} type="date" value={value} onChange={(event) => updateAnswer(question.id, event.target.value)} /> : null}
                    {question.questionType === "RATING" ? (options.length ? <RadioGroup name={`growth-${question.id}`} label={question.question} options={options.map((option) => ({ value: option, label: option }))} value={value} onChange={(answer) => updateAnswer(question.id, answer)} /> : <TextField id={`growth-${question.id}`} label={question.question} type="number" value={value} onChange={(event) => updateAnswer(question.id, event.target.value)} />) : null}
                    {question.questionType === "RADIO" ? <RadioGroup name={`growth-${question.id}`} label={question.question} options={options.map((option) => ({ value: option, label: option }))} value={value} onChange={(answer) => updateAnswer(question.id, answer)} orientation="vertical" /> : null}
                    {question.questionType === "CHECKBOX" ? (
                      <fieldset className={styles.choices}><legend>{question.question}</legend>
                        {options.map((option) => <Checkbox key={option} id={`growth-${question.id}-${option}`} label={option} checked={readChoices(value).includes(option)} onChange={(checked) => {
                          const current = readChoices(value);
                          const next = checked ? [...current, option] : current.filter((item) => item !== option);
                          updateAnswer(question.id, next.length ? JSON.stringify(next) : "");
                        }} />)}
                      </fieldset>
                    ) : null}
                    {question.questionType === "FILE" ? <p className={styles.stateText}>{question.question} — file upload is awaiting an API endpoint.</p> : null}
                  </div>
                );
              })}
              {hasFileQuestion ? <p className={styles.error}>This category includes a file question. Submission will be available when the upload API is ready.</p> : null}
              <div className={styles.formActions}><Button type="submit" disabled={hasFileQuestion}>Review answers <ArrowRight size={16} /></Button></div>
            </form>
          ) : null}
          {reviewing ? (
            <div className={styles.review}>
              <h2>Review your answers</h2>
              {submittedAnswers.map((answer) => <div className={styles.reviewAnswer} key={answer.question_id}><strong>{questions.find((question) => question.id === answer.question_id)?.question}</strong><span><LinkifiedText text={readChoices(answer.answer).length ? readChoices(answer.answer).join(", ") : answer.answer} /></span></div>)}
              {error ? <p className={styles.error} role="alert">{error}</p> : null}
              <div className={styles.formActions}><Button variant="muted-outline" onClick={() => setReviewing(false)}>Edit answers</Button><Button onClick={() => void submitRequest()} disabled={submitting}>{submitting ? "Submitting…" : "Submit initiative"}</Button></div>
            </div>
          ) : null}
        </section>
      ) : (
        <>
          <section className={styles.hero} aria-labelledby="growth-title">
            <div className={styles.intro}>
              <div className={styles.profile}><span className={styles.avatar}>{initials}</span><span><strong>{firstName}</strong><small>{user?.designation || "Employee"}</small></span></div>
              <div className={styles.introCopy}><p className={styles.eyebrow}>EXPLORE WHAT’S NEXT</p><h1 id="growth-title">What would you like to take forward?</h1><p>Select an area you’d like to focus on next. Feel free to return whenever a new idea or opportunity comes up.</p></div>
              <svg className={styles.journeyIllustration} viewBox="0 0 330 320" fill="none" aria-hidden="true" focusable="false">
                <defs>
                  <linearGradient id="growth-journey-peak" x1="156" y1="112" x2="293" y2="320" gradientUnits="userSpaceOnUse"><stop stopColor="#BBDDFB" /><stop offset="1" stopColor="#DDEFFF" /></linearGradient>
                  <linearGradient id="growth-journey-steps" x1="65" y1="220" x2="235" y2="320" gradientUnits="userSpaceOnUse"><stop stopColor="#EAF7FF" /><stop offset="1" stopColor="#B9E0FC" /></linearGradient>
                </defs>
                <path d="M182 74c0-11 9-20 20-20 10 0 18 7 20 16 3-2 7-3 11-3 11 0 20 8 20 19h-71c0-5 0-8 0-12Z" fill="#E3F3FE" />
                <path d="M82 145c1-11 10-19 21-19 9 0 17 6 20 15 3-2 6-3 10-3 10 0 18 7 19 17H82v-10Z" fill="#E4F4FE" />
                <path d="M193 320h123l-35-167c-2-11-8-16-19-16h-43c-10 0-16 5-19 16l-47 167h40Z" fill="url(#growth-journey-peak)" />
                <path d="M203 137h63c7 0 12 3 15 11l8 37c-29 4-67 5-97 2l9-37c2-8 5-13 12-13Z" fill="#D0E8FC" />
                <path d="M228 137V83" stroke="#50ADEB" strokeWidth="3" strokeLinecap="round" />
                <path d="m229 83 47 18-47 17V83Z" fill="#84CDF4" />
                <path d="M56 320h178l-15-105c-1-9-6-14-15-14h-37c-8 0-13 4-15 12l-12 40h-34c-8 0-12 4-15 11l-9 27H72c-7 0-11 3-13 9l-3 20Z" fill="url(#growth-journey-steps)" />
                <path d="M146 320h93l-14-101c-1-11-6-16-15-16h-35c-9 0-14 5-16 14l-13 103Z" fill="#C6E6FC" opacity=".82" />
                <path d="M57 320h131l-9-62c-1-8-5-12-13-12h-50c-8 0-12 4-14 11l-10 35H72c-7 0-11 3-13 9l-2 19Z" fill="#DEF1FF" opacity=".93" />
              </svg>
            </div>
            <div className={styles.categoryArea}>
              <div className={styles.categoryGrid}>
                {categories.map((category) => <button key={category.id} type="button" className={styles.categoryCard} data-tone={category.tone} onClick={() => void openCategory(category.id)}>
                  <span className={`${styles.categoryIcon} ${styles[category.tone]}`}><category.icon size={23} strokeWidth={1.8} /></span>
                  <span className={styles.categoryCopy}><strong>{category.title}</strong><small>{category.description}</small></span>
                  <ArrowRight className={styles.cardArrow} size={18} aria-hidden />
                </button>)}
              </div>
            </div>
          </section>
          <section className={styles.recent} aria-labelledby="recent-title">
            <div className={styles.recentHeader}><div><h2 id="recent-title">My Initiatives</h2><p>Recent initiatives and meaningful updates.</p></div>{requests.length > 3 ? <button type="button" className={styles.viewAll} onClick={() => setShowAll((value) => !value)}>{showAll ? "Show less" : "View all"}</button> : null}</div>
            {loadingRequests ? <p className={styles.stateText}>Loading your initiatives…</p> : null}
            {requestsError ? <div className={styles.stateText}><p role="alert">{requestsError}</p><Button variant="muted-outline" size="sm" onClick={() => void loadRequests()}>Try again</Button></div> : null}
            {!loadingRequests && !requestsError && requests.length === 0 ? <p className={styles.stateText}>Your initiatives will appear here after you submit one.</p> : null}
            {!loadingRequests && !requestsError && visibleRequests.length > 0 ? <div className={styles.requestList}>{visibleRequests.map((request) => {
              const category = categories.find((item) => item.id === request.growthCategory);
              const Icon = category?.icon || Rocket;
              return <div key={request.id} className={styles.requestItem}>
                <button type="button" className={styles.requestButton} onClick={() => setDetailId(request.id)}>
                  <span className={`${styles.categoryIcon} ${styles[category?.tone || "blue"]}`}><Icon size={20} /></span>
                  <span className={styles.requestCopy}><strong>{request.displayTitle || initiativeTitles[request.growthCategory] || "Your initiative"}</strong><small>{category?.title || request.growthCategory} · Updated {displayDate(request.updatedAt || request.createdAt)}</small></span>
                  <span className={styles.requestStatus}><span className={`${styles.statusDot} ${request.growthStatus === "RECEIVED" ? styles.receivedDot : ""}`} />{statusLabels[request.growthStatus] || request.growthStatus.replaceAll("_", " ").toLowerCase()}</span>
                  <ArrowRight size={17} aria-hidden />
                </button>
              </div>;
            })}</div> : null}
          </section>
        </>
      )}
    </PageShell>
  );
}
